import React, { useEffect, useState } from 'react';
import { X, AlertCircle, MessageSquare } from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';

interface Props {
  campaignId: string;
  recipientId: string;
  phone: string;
  onClose: () => void;
}

/**
 * What one recipient was actually sent on WhatsApp.
 *
 * <p>The stored message, never a re-render. Re-rendering answers "what would we send
 * now", which is a different question and wrong the moment the template is edited —
 * and the reason to open this is usually that somebody is disputing what they got.
 *
 * <p>Rendered as TEXT, not HTML. A WhatsApp body is plain text with light markup, so
 * there is nothing to interpret and interpreting it would only invite injection from
 * a value that came off a customer's spreadsheet.
 */
export const SentMessageModal: React.FC<Props> = ({ campaignId, recipientId, phone, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await whatsappService.getSentCopy(campaignId, recipientId);
      if (!alive) return;
      if (r.success) setData(r.data); else setError(r.error || 'Could not load the sent message');
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [campaignId, recipientId]);

  const params = (() => {
    if (!data?.templateParameters) return null;
    try {
      const p = typeof data.templateParameters === 'string'
        ? JSON.parse(data.templateParameters) : data.templateParameters;
      const entries = Object.entries(p || {}).filter(([, v]) => v != null && String(v) !== '');
      return entries.length ? entries : null;
    } catch { return null; }
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100 font-medium">
              <MessageSquare className="w-4 h-4 shrink-0" />
              <span className="truncate">{phone}</span>
            </div>
            {data?.sentAt && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Sent {new Date(data.sentAt).toLocaleString()}
                {data.deliveryStatus ? ` · ${data.deliveryStatus}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0 ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <p className="text-center text-gray-500 dark:text-gray-400 py-6">Loading…</p>}

          {error && (
            <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><p className="text-sm">{error}</p>
            </div>
          )}

          {!loading && !error && data && !data.stored && (
            <div className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" />
              <div className="text-sm">
                <p className="font-medium text-gray-800 dark:text-gray-100">No copy was recorded</p>
                {/* Explicit. "Nothing to show" would read as "nothing was sent". */}
                <p className="mt-1">{data.note || 'The send failed before the message was rendered, or WhatsApp never returned a message id.'}</p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Status {data.status}{data.errorCode ? ` · ${data.errorCode}` : ''}
                </p>
              </div>
            </div>
          )}

          {!loading && !error && data?.stored && (
            <>
              {/* The chat bubble, so it reads the way the recipient saw it. */}
              <div className="rounded-lg rounded-tl-none bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-3">
                {data.headerMedia && (
                  <img
                    src={data.headerMedia}
                    alt=""
                    className="w-full max-h-56 object-cover rounded mb-2"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                  {data.body}
                </p>
              </div>

              <dl className="text-xs space-y-1">
                {data.templateName && (
                  <div className="flex gap-2">
                    <dt className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Template</dt>
                    <dd className="text-gray-700 dark:text-gray-200">{data.templateName}</dd>
                  </div>
                )}
                {data.messageWamid && (
                  <div className="flex gap-2">
                    <dt className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Message id</dt>
                    <dd className="text-gray-700 dark:text-gray-200 break-all">{data.messageWamid}</dd>
                  </div>
                )}
                {params && (
                  <div className="flex gap-2">
                    <dt className="text-gray-400 dark:text-gray-500 w-24 shrink-0">Values used</dt>
                    <dd className="text-gray-700 dark:text-gray-200">
                      {params.map(([k, v]) => (
                        <span key={k} className="inline-block mr-2">
                          <span className="text-gray-400">{k}:</span> {String(v)}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          This is the message stored at send time, not a fresh render — editing the template does not change it.
        </div>
      </div>
    </div>
  );
};
